/**
 * dafClassify.ts
 * Pure classification logic for DAF influent analysis.
 * No React/JSX — safe to import from any context.
 */

/** Minimal parameter set required by classifyInfluent */
export interface ClassifyParams {
  ph: number;
  temp: number;
  cod: number;
  bod: number;
  tss: number;
  nh3n: number;
  og: number;
  conductivity: number;
  tp: number;
  alkalinity: number;
  hardness: number;
  coagulantDose: number;
}

export interface InfluentSource {
  id: string;
  label: string;
  probability: number;
  color: string;
}

export interface InfluentFlag {
  level: 'error' | 'warning' | 'info' | 'ok';
  code: string;
  message: string;
}

export interface InfluentClassification {
  sources: InfluentSource[];
  primarySource: InfluentSource;
  confidence: 'high' | 'medium' | 'low';
  flags: InfluentFlag[];
  conductivityAlarm: {
    triggered: boolean;
    level: 'critical' | 'high' | 'moderate' | 'normal';
    estTDS_mgL: number;
    salinity_ppt: number;
    message: string;
  };
  dafAdvice: {
    coagulantType: string;
    coagulantDoseHint: string;
    hlrHint: string;
    recycleHint: string;
    rationale: string;
  };
  dataQuality: 'good' | 'suspect' | 'invalid';
  dataQualityNote: string;
}

export function classifyInfluent(p: ClassifyParams): InfluentClassification {
  const bodCod = p.cod > 0 ? p.bod / p.cod : 0;
  const nh3Cod = p.cod > 0 ? p.nh3n / p.cod : 0;
  const ogTss  = p.tss > 0 ? p.og / p.tss : 0;

  // ── Data quality checks ──────────────────────────────────────────────────
  const flags: InfluentFlag[] = [];
  let dataQuality: 'good' | 'suspect' | 'invalid' = 'good';
  let dataQualityNote = '';

  if (p.bod > p.cod) {
    flags.push({ level: 'error', code: 'BOD_GT_COD',
      message: 'BOD > COD — physically impossible. Check sample data.' });
    dataQuality = 'invalid';
    dataQualityNote = 'BOD exceeds COD — data invalid';
  } else if (bodCod > 0.85) {
    flags.push({ level: 'warning', code: 'BOD_COD_HIGH',
      message: `BOD/COD ${bodCod.toFixed(2)} > 0.85 — unusually high. Verify lab results.` });
    if (dataQuality === 'good') { dataQuality = 'suspect'; dataQualityNote = 'BOD/COD ratio suspect'; }
  }

  if (bodCod < 0.08 && p.cod > 200) {
    flags.push({ level: 'warning', code: 'BOD_COD_LOW',
      message: `BOD/COD ${bodCod.toFixed(2)} < 0.08 — highly refractory. Biological treatment may be ineffective after DAF.` });
  } else if (bodCod < 0.20) {
    flags.push({ level: 'info', code: 'BOD_COD_MODERATE_LOW',
      message: `BOD/COD ${bodCod.toFixed(2)} — low biodegradability. Industrial or chemically complex effluent likely.` });
  } else if (bodCod >= 0.4 && bodCod <= 0.65) {
    flags.push({ level: 'ok', code: 'BOD_COD_TYPICAL',
      message: `BOD/COD ${bodCod.toFixed(2)} — typical range for food/municipal effluent. Good biodegradability.` });
  }

  if (p.ph < 4.5 || p.ph > 9.5) {
    flags.push({ level: 'warning', code: 'pH_EXTREME',
      message: `pH ${p.ph.toFixed(1)} — extreme. pH adjustment mandatory before DAF dosing.` });
  } else if (p.ph < 5.5 || p.ph > 8.5) {
    flags.push({ level: 'info', code: 'pH_MARGINAL',
      message: `pH ${p.ph.toFixed(1)} — outside neutral range. Monitor coagulant hydrolysis efficiency.` });
  }

  if (p.tss > 3000) {
    flags.push({ level: 'warning', code: 'TSS_HIGH',
      message: `TSS ${p.tss} mg/L extremely high — pre-screening or sedimentation recommended before DAF.` });
  } else if (p.tss > 1500) {
    flags.push({ level: 'info', code: 'TSS_ELEVATED',
      message: `TSS ${p.tss} mg/L elevated — consider coarse screening or grit removal.` });
  }

  if (p.og > 800) {
    flags.push({ level: 'warning', code: 'OG_HIGH',
      message: `O&G ${p.og} mg/L very high — emulsion risk. Pre-skim or dissolved air recycled stream degreaser may help.` });
  }

  if (p.nh3n > 100) {
    flags.push({ level: 'warning', code: 'NH3N_HIGH',
      message: `NH₃-N ${p.nh3n} mg/L — high nitrogen loading. DAF alone insufficient; biological post-treatment required.` });
  } else if (p.nh3n > 50) {
    flags.push({ level: 'info', code: 'NH3N_MODERATE',
      message: `NH₃-N ${p.nh3n} mg/L — moderate. Consider nitrification downstream.` });
  }

  if (p.alkalinity < 50 && p.coagulantDose > 8) {
    flags.push({ level: 'warning', code: 'ALKALINITY_LOW',
      message: `Alkalinity ${p.alkalinity} mg/L CaCO₃ — very low with coagulant dose ${p.coagulantDose} mg/L. Severe pH crash risk. Add NaOH/lime.` });
  }

  if (p.hardness > 500) {
    flags.push({ level: 'info', code: 'HARDNESS_HIGH',
      message: `Hardness ${p.hardness} mg/L CaCO₃ — scaling risk in saturator pipework and recycle pump.` });
  }

  if (nh3Cod > 0.04) {
    flags.push({ level: 'info', code: 'NH3_COD_RATIO',
      message: `NH₃-N/COD ratio ${nh3Cod.toFixed(3)} — proteinaceous source likely (meat, dairy, seafood).` });
  }

  if (ogTss > 0.6) {
    flags.push({ level: 'info', code: 'OG_TSS_RATIO',
      message: `O&G/TSS ratio ${ogTss.toFixed(2)} — high FOG fraction suggests meat processing, rendering or fryer effluent.` });
  }

  // ── Conductivity / Salinity Alarm ────────────────────────────────────────
  // TDS ≈ conductivity × 0.6 (empirical approximation for industrial wastewater)
  const estTDS = p.conductivity * 0.6;
  // Salinity (ppt) ≈ TDS / 1000 for NaCl-dominated brines
  const salinity_ppt = estTDS / 1000;

  let conductivityLevel: 'critical' | 'high' | 'moderate' | 'normal' = 'normal';
  let conductivityMessage = '';
  let conductivityTriggered = false;

  if (p.conductivity > 15000) {
    conductivityLevel = 'critical';
    conductivityMessage = `${p.conductivity.toLocaleString()} µS/cm — Extreme. Brine or AMD-level salinity (~${salinity_ppt.toFixed(1)} ppt). Coagulant efficiency severely impaired. RO pre-treatment may be required.`;
    conductivityTriggered = true;
    flags.push({ level: 'error', code: 'COND_CRITICAL',
      message: `Conductivity ${p.conductivity} µS/cm critical. Ionic shielding will heavily suppress coagulation.` });
  } else if (p.conductivity > 8000) {
    conductivityLevel = 'high';
    conductivityMessage = `${p.conductivity.toLocaleString()} µS/cm — High ionic strength (~${salinity_ppt.toFixed(1)} ppt est. salinity). Increase coagulant dose 20–30%. Check for brine, textile salt or mine drainage.`;
    conductivityTriggered = true;
    flags.push({ level: 'warning', code: 'COND_HIGH',
      message: `Conductivity ${p.conductivity} µS/cm — double-diffuse layer compression reduces floc formation.` });
  } else if (p.conductivity > 3000) {
    conductivityLevel = 'moderate';
    conductivityMessage = `${p.conductivity.toLocaleString()} µS/cm — Moderately elevated. Coagulant demand likely +10–15%. Monitor pH drop from coagulant hydrolysis.`;
    conductivityTriggered = true;
  }

  // ── Source Scoring ───────────────────────────────────────────────────────
  const rawScores: Record<string, number> = {};

  // DAIRY — COD 2k-12k, BOD:COD 0.35-0.60, moderate O&G, high TP, near-neutral pH
  {
    let s = 0;
    s += bodCod >= 0.35 && bodCod <= 0.62 ? 28 : bodCod >= 0.25 && bodCod <= 0.72 ? 14 : 0;
    s += p.cod >= 2000 && p.cod <= 12000 ? 18 : p.cod >= 1000 && p.cod < 2000 ? 8 : 0;
    s += p.og >= 40 && p.og <= 250  ? 18 : p.og >= 15 && p.og < 40 ? 8 : 0;
    s += p.tp >= 4 && p.tp <= 25    ? 14 : p.tp >= 2 ? 6 : 0;
    s += p.ph >= 5.0 && p.ph <= 7.5 ? 12 : p.ph >= 4.5 && p.ph <= 8.5 ? 5 : 0;
    s += p.tss >= 150 && p.tss <= 1500 ? 10 : p.tss < 150 || p.tss <= 2500 ? 4 : 0;
    rawScores.dairy = s;
  }

  // BREWERY / DISTILLERY — very high COD, high BOD:COD, acidic pH, very low TSS, low O&G
  {
    let s = 0;
    s += bodCod >= 0.48 && bodCod <= 0.85 ? 28 : bodCod >= 0.38 ? 14 : 0;
    s += p.cod >= 3000            ? 16 : p.cod >= 1500 ? 8 : 0;
    s += p.ph >= 3.5 && p.ph <= 6.2 ? 22 : p.ph >= 3.0 && p.ph <= 7.0 ? 10 : 0;
    s += p.og <= 30               ? 16 : p.og <= 80 ? 8 : 0;
    s += p.tss <= 400             ? 14 : p.tss <= 800 ? 6 : 0;
    s += p.tp >= 5 && p.tp <= 35  ? 4  : p.tp > 2 ? 2 : 0;
    rawScores.brewery = s;
  }

  // ABATTOIR / MEAT — high BOD, very high TSS, very high O&G, high NH₃-N, near-neutral pH
  {
    let s = 0;
    s += p.tss >= 1000            ? 22 : p.tss >= 500 ? 11 : 0;
    s += p.og >= 300              ? 22 : p.og >= 120  ? 11 : 0;
    s += p.nh3n >= 60             ? 20 : p.nh3n >= 25 ? 10 : 0;
    s += p.ph >= 6.5 && p.ph <= 8.0 ? 14 : p.ph >= 6.0 && p.ph <= 8.5 ? 6 : 0;
    s += p.bod >= 1500            ? 12 : p.bod >= 600  ? 6  : 0;
    s += bodCod >= 0.3 && bodCod <= 0.65 ? 10 : 4;
    rawScores.abattoir = s;
  }

  // SEAFOOD / FISH — high O&G, elevated conductivity (sea salt), high NH₃, moderate COD, cold
  {
    let s = 0;
    s += p.og >= 120              ? 20 : p.og >= 50 ? 10 : 0;
    s += p.conductivity >= 2000 && p.conductivity <= 9000 ? 18 : p.conductivity > 1000 ? 8 : 0;
    s += p.nh3n >= 20 && p.nh3n <= 150 ? 18 : p.nh3n >= 10 ? 8 : 0;
    s += p.tss >= 200 && p.tss <= 1500  ? 14 : p.tss >= 80 ? 6 : 0;
    s += p.ph >= 6.0 && p.ph <= 7.8    ? 14 : p.ph >= 5.5 && p.ph <= 8.5 ? 5 : 0;
    s += p.temp <= 18                   ? 12 : p.temp <= 22 ? 6 : 0;
    s += p.cod >= 1000 && p.cod <= 6000 ? 4  : 0;
    rawScores.seafood = s;
  }

  // TEXTILE — low BOD:COD, alkaline or acid pH (dye baths), high conductivity (salt), high COD
  {
    let s = 0;
    s += bodCod < 0.22            ? 28 : bodCod < 0.33 ? 14 : 0;
    s += p.ph >= 9.0 || p.ph <= 5.0 ? 24 : p.ph >= 8.5 || p.ph <= 5.5 ? 12 : 0;
    s += p.conductivity >= 3000   ? 22 : p.conductivity >= 1500 ? 10 : 0;
    s += p.cod >= 1500            ? 14 : p.cod >= 700 ? 6 : 0;
    s += p.og <= 30               ? 8  : p.og <= 60 ? 4 : 0;
    s += p.tss >= 200             ? 4  : 0;
    rawScores.textile = s;
  }

  // CHEMICAL / INDUSTRIAL — very low BOD:COD, possible extreme pH, high conductivity
  {
    let s = 0;
    s += bodCod < 0.18            ? 30 : bodCod < 0.28 ? 15 : 0;
    s += p.ph < 4.0 || p.ph > 9.5 ? 24 : p.ph < 5.0 || p.ph > 9.0 ? 12 : 0;
    s += p.conductivity >= 5000   ? 22 : p.conductivity >= 2000 ? 10 : 0;
    s += p.cod >= 2000            ? 14 : p.cod >= 800 ? 6 : 0;
    s += p.tss <= 250             ? 10 : 0;
    rawScores.chemical = s;
  }

  // MUNICIPAL / DOMESTIC — moderate, balanced, BOD:COD 0.4-0.65, moderate NH₃
  {
    let s = 0;
    s += bodCod >= 0.38 && bodCod <= 0.67 ? 24 : bodCod >= 0.28 && bodCod <= 0.75 ? 12 : 0;
    s += p.cod >= 150 && p.cod <= 900     ? 28 : p.cod >= 80 && p.cod <= 1500 ? 12 : 0;
    s += p.ph >= 6.5 && p.ph <= 8.2       ? 16 : p.ph >= 6.0 && p.ph <= 8.5 ? 7 : 0;
    s += p.nh3n >= 10 && p.nh3n <= 70     ? 16 : p.nh3n >= 5 ? 7 : 0;
    s += p.conductivity >= 300 && p.conductivity <= 1500 ? 14 : p.conductivity < 2500 ? 5 : 0;
    s += p.og <= 60                        ? 2  : 0;
    rawScores.municipal = s;
  }

  // MINING / AMD — very low pH, very high conductivity, very low BOD, low O&G, high TSS (fines)
  {
    let s = 0;
    s += p.ph < 3.5               ? 38 : p.ph < 4.5 ? 22 : p.ph < 5.5 ? 10 : 0;
    s += p.conductivity >= 5000   ? 28 : p.conductivity >= 2500 ? 14 : 0;
    s += p.bod <= 80              ? 16 : p.bod <= 250 ? 7 : 0;
    s += p.og <= 15               ? 14 : p.og <= 40 ? 6 : 0;
    s += p.tss >= 400             ? 4  : 0;
    rawScores.mining = s;
  }

  // FOOD PROCESSING (general) — moderate everything
  {
    let s = 0;
    s += bodCod >= 0.28 && bodCod <= 0.68 ? 18 : bodCod >= 0.20 && bodCod <= 0.78 ? 8 : 0;
    s += p.cod >= 500 && p.cod <= 5000     ? 22 : p.cod >= 200 && p.cod <= 8000 ? 10 : 0;
    s += p.og >= 20 && p.og <= 250         ? 18 : p.og >= 10 ? 8 : 0;
    s += p.ph >= 5.5 && p.ph <= 8.0       ? 16 : p.ph >= 5.0 && p.ph <= 8.5 ? 7 : 0;
    s += p.tss >= 100 && p.tss <= 1200     ? 14 : p.tss >= 50 ? 6 : 0;
    s += p.nh3n >= 8 && p.nh3n <= 60      ? 12 : p.nh3n >= 3 ? 5 : 0;
    rawScores.food = s;
  }

  // ── Normalize + label ────────────────────────────────────────────────────
  const sourcesMeta: Record<string, { label: string; color: string }> = {
    dairy:    { label: 'Dairy / Milk Processing', color: '#60a5fa' },
    brewery:  { label: 'Brewery / Distillery',    color: '#f59e0b' },
    abattoir: { label: 'Abattoir / Meat',         color: '#ef4444' },
    seafood:  { label: 'Seafood / Fish',          color: '#06b6d4' },
    textile:  { label: 'Textile / Laundry',       color: '#a78bfa' },
    chemical: { label: 'Chemical / Industrial',   color: '#f97316' },
    municipal:{ label: 'Municipal / Domestic',    color: '#34d399' },
    mining:   { label: 'Mining / AMD',            color: '#fb923c' },
    food:     { label: 'General Food Processing', color: '#facc15' },
  };

  const totalScore = Object.values(rawScores).reduce((a, b) => a + b, 0) || 1;
  const sources: InfluentSource[] = Object.entries(rawScores)
    .map(([id, s]) => ({
      id,
      label: sourcesMeta[id].label,
      color: sourcesMeta[id].color,
      probability: Math.round((s / totalScore) * 100),
    }))
    .sort((a, b) => b.probability - a.probability);

  const primarySource = sources[0];
  const confidence: 'high' | 'medium' | 'low' =
    primarySource.probability >= 50 ? 'high' :
    primarySource.probability >= 32 ? 'medium' : 'low';

  // ── DAF Advice based on primary source ──────────────────────────────────
  const dafAdviceMap: Record<string, InfluentClassification['dafAdvice']> = {
    dairy: {
      coagulantType: 'Ferric chloride or PAC',
      coagulantDoseHint: '10–20 mg/L (adjust for TP removal)',
      hlrHint: '3.5–5.0 m³/m²·h',
      recycleHint: '25–35 %',
      rationale: 'Dairy proteins and fats respond well to ferric with slight pH reduction to 6.0–6.5. Anionic polymer aids floc settling. High TP may warrant enhanced coagulation.',
    },
    brewery: {
      coagulantType: 'PAC or Aluminium sulphate',
      coagulantDoseHint: '10–18 mg/L (pH is already acidic)',
      hlrHint: '4.0–6.0 m³/m²·h',
      recycleHint: '25–30 %',
      rationale: 'Brewery effluent is acidic and high BOD with colloidal yeast/hops. Alum or PAC works at lower pH. Add alkali to maintain coagulation pH 5.5–6.5. A/S ratio 0.005–0.010.',
    },
    abattoir: {
      coagulantType: 'Ferric chloride',
      coagulantDoseHint: '15–25 mg/L (high O&G and TSS)',
      hlrHint: '2.5–4.0 m³/m²·h',
      recycleHint: '30–40 %',
      rationale: 'Blood proteins, FOG and TSS are best treated with ferric + cationic polymer. Lower HLR ensures bubble contact time for heavy float blanket. High recycle for good A/S ratio.',
    },
    seafood: {
      coagulantType: 'Ferric chloride or PAC',
      coagulantDoseHint: '12–22 mg/L',
      hlrHint: '3.0–4.5 m³/m²·h',
      recycleHint: '28–35 %',
      rationale: 'Fish-processing effluent has oily proteinaceous solids. Ferric at pH 6.0–7.0 performs well. Elevated conductivity from salt will compress coagulant double-layer — increase dose 15%.',
    },
    textile: {
      coagulantType: 'Ferric chloride or PAC',
      coagulantDoseHint: '20–35 mg/L (colour removal)',
      hlrHint: '3.0–5.0 m³/m²·h',
      recycleHint: '25–35 %',
      rationale: 'Textile dye removal requires higher coagulant doses and acidic conditions (pH 4.5–6.0). Fenton pre-oxidation or ozone upstream can decolourise before DAF.',
    },
    chemical: {
      coagulantType: 'Ferric or specialised coagulant',
      coagulantDoseHint: '15–30 mg/L (lab jar-test recommended)',
      hlrHint: '3.0–5.0 m³/m²·h',
      recycleHint: '25–35 %',
      rationale: 'Industrial chemical waste is highly variable. Lab jar-test essential. Low BOD:COD indicates refractory compounds — biological post-treatment efficacy will be low.',
    },
    municipal: {
      coagulantType: 'Alum or PAC',
      coagulantDoseHint: '8–15 mg/L',
      hlrHint: '4.5–6.5 m³/m²·h',
      recycleHint: '20–30 %',
      rationale: 'Municipal wastewater is relatively dilute and consistent. PAC or alum at standard dose is effective. Higher HLR acceptable due to lower TSS loading.',
    },
    mining: {
      coagulantType: 'Lime + ferric (AMD neutralisation)',
      coagulantDoseHint: 'Lime to pH 8.5–9.5, then ferric 10–20 mg/L',
      hlrHint: '2.0–3.5 m³/m²·h (lamella preferred)',
      recycleHint: '25–30 %',
      rationale: 'Acid Mine Drainage requires lime neutralisation first to precipitate Fe/Mn/Zn as hydroxides. DAF or lamella clarifier then captures the precipitate. Very high sludge volume expected.',
    },
    food: {
      coagulantType: 'Ferric chloride or PAC',
      coagulantDoseHint: '12–20 mg/L',
      hlrHint: '3.5–5.5 m³/m²·h',
      recycleHint: '25–35 %',
      rationale: 'General food processing responds well to ferric with anionic polymer. Ensure pH 6.0–7.0. Monitor O&G for emulsion risk.',
    },
  };

  const dafAdvice = dafAdviceMap[primarySource.id] ?? dafAdviceMap.food;

  return {
    sources,
    primarySource,
    confidence,
    flags,
    conductivityAlarm: {
      triggered: conductivityTriggered,
      level: conductivityLevel,
      estTDS_mgL: Math.round(estTDS),
      salinity_ppt: parseFloat(salinity_ppt.toFixed(2)),
      message: conductivityMessage,
    },
    dafAdvice,
    dataQuality,
    dataQualityNote,
  };
}
